import { assertCapabilities } from 'Store/index';
import { isString, isTPlatformCapability } from 'Utility/typeGuards';
import PlatformCapabilities from '../../../config/capabilities';
import { PlatformToArchiveMap } from '../../../config/platformSettings';

import type { INeuroTemplatedClinicalArchiveDocument } from 'neuro-data-structures';

/**
 * Check which archive doc types are available in the session.
 * @param groupMapping Groups to capabilities map.
 * @returns Array of available archive doc types.
 */
export const getAvailableArchiveTypes = (groupMapping: IGroupACLMapping, platform: Platform): Array<string> => {
  const platfArchiveDocs = PlatformToArchiveMap[platform];
  const assertedArchiveTypes = platfArchiveDocs.map(d => {
    const capability = `${platform.toUpperCase()}_RECORD_${d}`;
    if (!isTPlatformCapability(capability)) return;
    const pCapability = PlatformCapabilities[capability as keyof typeof PlatformCapabilities];
    return assertCapabilities([pCapability], groupMapping) ? d : undefined;
  });
  const pruned: Array<string> = [];
  assertedArchiveTypes.forEach(d => {
    if (isString(d)) pruned.push(d);
  });
  return pruned;
};

/**
 * Archive object that has been fetched from api and sorted
 */
export interface IArchiveObject extends INeuroTemplatedClinicalArchiveDocument {
  /** Timestamp for the first revision of the document */
  firstTimestamp?: number;
}

/**
 * Object describing changes to text blocks withing the clinical document
 */
export interface IClinicalTextObject {
  /** sectionIndex, number, "Index of the clinical archive document section that should be updated" */
  sectionIndex: number;
  /** contentIndex, number, "Index of the section textContent block that should be updated" */
  contentIndex: number;
  /** text, string, "Optional new text value of the section text content block, overwrites completely. May be set to null to nullify the content text field. If left out then the text value is not updated." */
  text?: string | null;
  /** exclude, boolean, "Optional new section text content block exclusion state. If left out then excluded state if not updated" */
  exclude?: boolean;
  /** dataStartTimeStamp, number, "Optional override of archive document dataStartTimestamp that this blocks textContent compiler should use instead of the archive objects main dataStartTimestamp." */
  dataStartTimestamp?: number | null;
  /** dataEndTimeStamp, number, "Optional override of archive document dataEndTimeStamp that this blocks textContent compiler should use instead of the archive objects main dataEndTimeStamp." */
  dataEndTimestamp?: number | null;
}

/**
 * Process an array of clinical archive documents to produce a de-duplicated list for display.
 * The function groups documents by their logical identity, selects the latest revision and attaches the creation timestamp of the very first revision.
 */
export const sortAndFilterArchives = (data: INeuroTemplatedClinicalArchiveDocument[]): IArchiveObject[] => {
  if (data.length === 0) return [];

  const removedFiltered = data.filter(d => !d.isRemoved);

  // Use a Map to group documents by a truly unique key
  const archivesMap = new Map<string, IArchiveObject>();

  for (const doc of removedFiltered) {
    const key = `${doc.patientId}|${doc.archiveId}|${doc.archiveType}|${doc.diseaseModule}`;

    const existingEntry = archivesMap.get(key);

    if (!existingEntry) {
      // If we haven't seen this archive group before, add it
      // The first time we see it, its creationTimestamp is the firstTimestamp
      archivesMap.set(key, { ...doc, firstTimestamp: doc.creationTimestamp });
    } else {
      // If the entry exists, decide whether to update it

      // 1. Find the document with the highest revision number to be the "master" record
      const latestDoc = doc.archiveRevision > existingEntry.archiveRevision ? doc : existingEntry;

      // 2. Find the earliest creation timestamp across all revisions
      const firstTimestamp = Math.min(doc.creationTimestamp, existingEntry.firstTimestamp!);

      archivesMap.set(key, {
        ...latestDoc,
        firstTimestamp,
      });
    }
  }

  // Get the unique, processed documents from the map's values
  const uniqueArchives = Array.from(archivesMap.values());

  // Sort one last time
  return uniqueArchives.sort((a, b) =>
    a.archiveId === null ? -1 : b.archiveId === null ? 1 : (b.firstTimestamp || 0) - (a.firstTimestamp || 0),
  );
};
